import { Shadows } from '../../../../theme';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import {
  Popover as ReactAriaPopover,
  PopoverProps as ReactAriaPopoverProps,
  useContextProps,
} from 'react-aria-components';
import { mergeClassNames } from '../../../../utils/mergeClassNames';
import styles from './Popover.module.scss';
import { SelectBaseProps } from './SelectBase';

export type SelectBasePopoverProps = ReactAriaPopoverProps & {
  /**
   * The size, impacting the popover's dimensions.
   * @default 'medium'
   */
  size?: SelectBaseProps['size'];
};

export type SelectBasePopoverContextValue = ContextValue<
  Omit<SelectBasePopoverProps, 'children'>,
  HTMLDivElement
>;

export const SelectBasePopoverContext =
  createContext<SelectBasePopoverContextValue>({});

/**
 * A styled Popover from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `SelectBasePopoverContext`.
 */
export const SelectBasePopover = (props: SelectBasePopoverProps) => {
  const [contextProps] = useContextProps(props, null, SelectBasePopoverContext);
  const { className, size = 'medium', style, ...otherProps } = contextProps;

  return (
    <ReactAriaPopover
      {...otherProps}
      style={{
        ...style,
        '--popover-shadow': Shadows.elevation20,
      }}
      className={(renderProps) =>
        mergeClassNames([styles.popover, className], renderProps)
      }
      data-size={size}
    />
  );
};
